// The pedigree data-overlay catalogue (TGCLOUD-1670).
//
// DEPENDENCY-FREE ON PURPOSE. `scripts/va-vocab/generate.js` requires this file
// under @babel/register, exactly as it requires `sidebarValues.js`, and emits the
// summaries into both va-vocabulary.json artifacts. One source therefore feeds
// the Overlays picker, the Copilot tool enum, the deterministic (no-AI) matcher
// and CI. Import nothing here: the evaluator owns the imports.
//
// A shared constant is referenced by NAME (`{ ref: 'RESOLVED_APPOINTMENT_STATUSES' }`)
// and resolved at materialise time, so the vocabulary lives in exactly one place
// (src/util/recordDueDates.js) without this file importing it.
//
// Spec shape, in full:
//   source  { type: 'note', noteType }        the person's notes of that type
//           { type: 'member' }                 the member record, one row
//           { type: 'memberArray', path }      elements of a member array
//   where   [{ field, op, value }]             ANDed over ONE record or element
//   reduce  { kind: 'any' | 'none' | 'count' | 'latest' | 'earliest' | 'min' | 'max',
//             field }                          field required for the last four
//   then    { op, value }                      optional, tests the reduced value
//   and     [sub-spec]                         person-level AND, depth 1
//   andNot  [sub-spec]                         person-level AND NOT, depth 1
//
// Values may be literals, `{ param: 'key' }`, `{ ref: 'CONSTANT' }`, or a
// relative date `{ rel: 'today', offsetDays }` where offsetDays may itself be
// `{ param, negate }`.
//
// `pill` is at most 6 characters. Colour belongs to the slot, never the datum,
// so the pill text and the legend carry the meaning (cascadeBadge.js:1-6 makes
// the same rule for the cascade badge: colour is never the sole signal).

export const OVERLAY_CATEGORIES = Object.freeze([
  'Appointments',
  'Contacts',
  'Referrals',
  'Consent',
  'Samples and tests',
  'Records',
  'Person',
  'Clinical'
]);

const RESOLVED = { ref: 'RESOLVED_APPOINTMENT_STATUSES' };

export const DATA_OVERLAY_PRESETS = Object.freeze([
  // ── Appointments ────────────────────────────────────────────────────────
  {
    id: 'appointment.outstanding',
    title: 'Outstanding appointment',
    description:
      'Family members with an appointment whose date has passed and which was never closed out. Cloud records attendance on a contact record, not on the appointment, so this is the honest reading of "booked and not attended".',
    category: 'Appointments',
    roles: 'facts',
    intents: [
      'who has an outstanding appointment',
      'who was booked in but never seen',
      'show appointments that were never closed off',
      'who still needs an appointment closing out',
      'which relatives have an open appointment in the past',
      'appointments booked and not attended'
    ],
    pill: 'APPT',
    countPill: true,
    weight: 'dueBands',
    weightField: '$daysSince(appointmentDate)',
    paramDefs: [],
    spec: {
      source: { type: 'note', noteType: 'Appointment' },
      where: [
        { field: 'appointmentStatus', op: 'notIn', value: RESOLVED },
        { field: 'appointmentDate', op: 'lessThan', value: { rel: 'today' } }
      ],
      reduce: { kind: 'count' },
      then: { op: 'greaterThan', value: 0 }
    }
  },
  {
    id: 'appointment.closedInPeriod',
    title: 'Seen in clinic',
    description:
      'Family members seen at an appointment within the last N days. Answers "who has been seen" for a family review.',
    category: 'Appointments',
    roles: 'facts',
    intents: [
      'who has been seen in clinic',
      'who has attended an appointment recently',
      'show me who has been seen this year',
      'which relatives have been to clinic',
      'who has had an appointment lately'
    ],
    pill: 'SEEN',
    paramDefs: [
      {
        key: 'lookbackDays',
        type: 'int',
        label: 'Seen within (days)',
        min: 1,
        max: 3650,
        default: 365,
        bands: [90, 365, 730]
      }
    ],
    spec: {
      source: { type: 'note', noteType: 'Appointment' },
      where: [
        { field: 'appointmentStatus', op: 'in', value: RESOLVED },
        {
          field: 'appointmentDate',
          op: 'greaterThanOrEqual',
          value: {
            rel: 'today',
            offsetDays: { param: 'lookbackDays', negate: true }
          }
        }
      ],
      reduce: { kind: 'latest', field: 'appointmentDate' }
    }
  },
  {
    id: 'appointment.never',
    title: 'No appointment on record',
    description:
      'Family members with no appointment record of any kind. A gap-spotting overlay for a family that has never been brought into clinic.',
    category: 'Appointments',
    roles: 'facts',
    intents: [
      'who has never had an appointment',
      'who has never been seen',
      'which relatives have no appointment',
      'show me the people with no clinic record',
      'who has not been brought in yet'
    ],
    pill: '0 APPT',
    paramDefs: [],
    spec: {
      source: { type: 'note', noteType: 'Appointment' },
      where: [],
      reduce: { kind: 'none' }
    }
  },

  // ── Contacts ────────────────────────────────────────────────────────────
  {
    id: 'contact.reminderOverdue',
    title: 'Overdue contact reminder',
    description:
      'Family members with a contact reminder whose date has passed, where the contact is not completed or cancelled.',
    category: 'Contacts',
    roles: 'facts',
    intents: [
      'who has an overdue contact',
      'show overdue contact reminders',
      'who should I have contacted by now',
      'which contacts are late',
      'who is overdue for a follow up',
      'show me overdue follow ups on the pedigree'
    ],
    pill: { field: '$daysSince(reminderDate)', suffix: 'd' },
    weight: 'dueBands',
    weightField: '$daysSince(reminderDate)',
    paramDefs: [],
    spec: {
      source: { type: 'note', noteType: 'Contact' },
      where: [
        { field: 'reminderEnabled', op: 'equals', value: true },
        { field: 'reminderDate', op: 'lessThan', value: { rel: 'today' } },
        {
          field: 'contactStatus',
          op: 'notIn',
          value: ['Sent / Completed', 'Cancelled']
        }
      ],
      reduce: { kind: 'min', field: '$daysUntil(reminderDate)' }
    }
  },
  {
    id: 'contact.didNotAttend',
    title: 'Did not attend',
    description:
      'Family members with a contact recorded as Did Not Attend. This is the only place attendance is recordable in Cloud.',
    category: 'Contacts',
    roles: 'facts',
    intents: [
      'who did not attend',
      'show me the did not attend records',
      'who missed their appointment',
      'which relatives failed to attend',
      'who has a DNA recorded'
    ],
    pill: 'DNA',
    paramDefs: [
      {
        key: 'lookbackDays',
        type: 'int',
        label: 'Within (days)',
        min: 1,
        max: 3650,
        default: 365,
        bands: [90, 365, 3650]
      }
    ],
    spec: {
      source: { type: 'note', noteType: 'Contact' },
      where: [
        { field: 'contactStatus', op: 'equals', value: 'Did Not Attend' },
        {
          field: 'contactDate',
          op: 'greaterThanOrEqual',
          value: {
            rel: 'today',
            offsetDays: { param: 'lookbackDays', negate: true }
          }
        }
      ],
      reduce: { kind: 'latest', field: 'contactDate' }
    }
  },
  {
    id: 'contact.never',
    title: 'Never contacted',
    description:
      'Family members with no contact record at all. The core cascade-screening gap: relatives nobody has written to or called.',
    category: 'Contacts',
    roles: 'facts',
    intents: [
      'who has never been contacted',
      'who have we not contacted',
      'show me relatives with no contact record',
      'who still needs contacting',
      'who to contact next',
      'which relatives have never been written to'
    ],
    pill: '0 CONT',
    paramDefs: [],
    spec: {
      source: { type: 'note', noteType: 'Contact' },
      where: [],
      reduce: { kind: 'none' }
    }
  },

  // ── Referrals ───────────────────────────────────────────────────────────
  {
    id: 'referral.open',
    title: 'Open referral',
    description:
      'Family members with a referral that has not been closed. Treats Open, Accepted and a missing status as open.',
    category: 'Referrals',
    roles: 'facts',
    intents: [
      'who has an open referral',
      'show open referrals',
      'which referrals are still outstanding',
      'who was referred and not closed',
      'show me referrals in progress'
    ],
    pill: 'REF',
    weight: 'dueBands',
    weightField: '$daysSince(referralDate)',
    paramDefs: [],
    spec: {
      source: { type: 'note', noteType: 'Referral' },
      where: [{ field: 'referralStatus', op: 'notEquals', value: 'Closed' }],
      reduce: { kind: 'earliest', field: 'referralDate' }
    }
  },
  {
    id: 'referral.openNoAppointment',
    title: 'Referred, no appointment',
    description:
      'Family members with an open referral and no appointment record at all: the referral-to-clinic gap.',
    category: 'Referrals',
    roles: 'facts',
    intents: [
      'who was referred but never given an appointment',
      'show referrals with no appointment',
      'which referrals have not reached clinic',
      'who is waiting for an appointment after referral',
      'spot the gap between referral and clinic'
    ],
    pill: 'REF/0A',
    paramDefs: [],
    spec: {
      source: { type: 'member' },
      where: [],
      reduce: { kind: 'any' },
      and: [
        {
          source: { type: 'note', noteType: 'Referral' },
          where: [
            { field: 'referralStatus', op: 'notEquals', value: 'Closed' }
          ],
          reduce: { kind: 'any' }
        }
      ],
      andNot: [
        {
          source: { type: 'note', noteType: 'Appointment' },
          where: [],
          reduce: { kind: 'any' }
        }
      ]
    }
  },

  // ── Consent ─────────────────────────────────────────────────────────────
  {
    id: 'consent.given',
    title: 'Consent given',
    description:
      'Family members whose most recent consent record says consent was given.',
    category: 'Consent',
    roles: 'facts',
    intents: [
      'who has consented',
      'show me who has given consent',
      'which relatives have consent on file',
      'who has consent recorded',
      'show consented family members'
    ],
    pill: 'CONS',
    paramDefs: [],
    spec: {
      source: { type: 'note', noteType: 'Consent' },
      where: [{ field: 'response', op: 'equals', value: 'Consent given' }],
      reduce: { kind: 'latest', field: 'consentDate' }
    }
  },
  {
    id: 'consent.none',
    title: 'No consent on file',
    description:
      'Family members with no consent record at all. Pairs with a clinical filter to find people who need consenting before testing.',
    category: 'Consent',
    roles: 'facts',
    intents: [
      'who has no consent',
      'which relatives are missing consent',
      'show me people without consent on file',
      'who still needs to consent',
      'who has not consented yet'
    ],
    pill: '0 CONS',
    paramDefs: [],
    spec: {
      source: { type: 'note', noteType: 'Consent' },
      where: [],
      reduce: { kind: 'none' }
    }
  },

  // ── Samples and tests ───────────────────────────────────────────────────
  {
    id: 'test.resultOutstanding',
    title: 'Test result outstanding',
    description: 'Family members with a test requested and no result received.',
    category: 'Samples and tests',
    roles: 'facts',
    intents: [
      'who is waiting for a test result',
      'show tests with no result',
      'which results are outstanding',
      'who has a test in progress',
      'show me pending test results'
    ],
    pill: 'TEST?',
    paramDefs: [],
    spec: {
      source: { type: 'note', noteType: 'Test' },
      where: [
        { field: 'dateRequested', op: 'exists' },
        { field: 'dateResultReceived', op: 'notExists' }
      ],
      reduce: { kind: 'earliest', field: 'dateRequested' }
    }
  },
  {
    id: 'test.resultOverdue',
    title: 'Test result overdue',
    description:
      'Family members whose test result due date has passed with no result received.',
    category: 'Samples and tests',
    roles: 'facts',
    intents: [
      'which test results are overdue',
      'who is past their result due date',
      'show late test results',
      'which labs have not reported back',
      'show me overdue results on the pedigree'
    ],
    pill: { field: '$daysSince(dateResultDue)', suffix: 'd' },
    weight: 'dueBands',
    weightField: '$daysSince(dateResultDue)',
    paramDefs: [],
    spec: {
      source: { type: 'note', noteType: 'Test' },
      where: [
        { field: 'dateResultReceived', op: 'notExists' },
        { field: 'dateResultDue', op: 'lessThan', value: { rel: 'today' } }
      ],
      reduce: { kind: 'min', field: '$daysUntil(dateResultDue)' }
    }
  },
  {
    id: 'test.never',
    title: 'Never tested',
    description:
      'Family members with no test record at all. Compose with a carrier or diagnosis filter to find untested at-risk relatives.',
    category: 'Samples and tests',
    roles: 'facts',
    intents: [
      'who has never been tested',
      'which relatives have no test',
      'show me untested family members',
      'who still needs testing',
      'show people with no genetic test'
    ],
    pill: '0 TEST',
    paramDefs: [],
    spec: {
      source: { type: 'note', noteType: 'Test' },
      where: [],
      reduce: { kind: 'none' }
    }
  },

  // ── Records ─────────────────────────────────────────────────────────────
  {
    id: 'address.noCorrespondence',
    title: 'No correspondence address',
    description:
      'Family members with no address flagged as the correspondence address, so a letter cannot be sent.',
    category: 'Records',
    roles: 'facts',
    intents: [
      'who has no correspondence address',
      'which relatives cannot be written to',
      'show people with a missing address',
      'who is missing an address for letters',
      'show me address gaps'
    ],
    pill: '0 ADDR',
    paramDefs: [],
    spec: {
      source: { type: 'member' },
      where: [
        { field: '$address.hasCorrespondence', op: 'notEquals', value: true }
      ],
      reduce: { kind: 'any' }
    }
  },
  {
    id: 'process.displayInPedigree',
    title: 'Marked for pedigree',
    description:
      'Family members with a process record flagged Display in Pedigree. This is the TG Desktop flag, preserved on import and until now read by nothing.',
    category: 'Records',
    roles: 'facts',
    intents: [
      'show processes marked for the pedigree',
      'which records are flagged to display',
      'show the desktop display in pedigree flag',
      'who has a process marked for pedigree',
      'show imported pedigree markers'
    ],
    pill: 'PROC',
    countPill: true,
    paramDefs: [],
    spec: {
      source: { type: 'note', noteType: 'Process' },
      where: [{ field: 'displayInPedigree', op: 'equals', value: true }],
      reduce: { kind: 'count' },
      then: { op: 'greaterThan', value: 0 }
    }
  },
  {
    id: 'clinicalNote.caution',
    title: 'Caution flag',
    description:
      'Family members with a clinical note flagged as caution. The same flag the sidebar shows as a red border and a star.',
    category: 'Records',
    roles: 'facts',
    defaultOff: true,
    intents: [
      'who has a caution flag',
      'show cautions on the pedigree',
      'which relatives have a warning note',
      'show me flagged clinical notes',
      'who has a caution recorded'
    ],
    pill: '!',
    paramDefs: [],
    spec: {
      source: { type: 'note', noteType: 'Clinical Note' },
      where: [{ field: 'caution', op: 'equals', value: true }],
      reduce: { kind: 'any' }
    }
  },

  // ── Person ──────────────────────────────────────────────────────────────
  {
    id: 'member.recordsNotReviewed',
    title: 'Records not reviewed',
    description:
      'Family members whose records have not been marked as reviewed. The inverse of the asterisk already drawn at the node.',
    category: 'Person',
    roles: 'member',
    intents: [
      'whose records have not been reviewed',
      'show unreviewed records',
      'which people still need reviewing',
      'who has not been checked off',
      'show me the review backlog'
    ],
    pill: 'REVIEW',
    paramDefs: [],
    spec: {
      source: { type: 'member' },
      where: [{ field: 'recordsReviewed', op: 'notEquals', value: true }],
      reduce: { kind: 'any' }
    }
  },
  {
    id: 'member.tag',
    title: 'Has person tag',
    description: 'Family members carrying one of the chosen person tags.',
    category: 'Person',
    roles: 'member',
    intents: [
      'who has a tag',
      'show me people with a particular tag',
      'highlight tagged relatives',
      'which people are tagged',
      'show person tags on the pedigree'
    ],
    pill: 'TAG',
    paramDefs: [
      {
        key: 'tags',
        type: 'multiEnum',
        label: 'Tags',
        optionsSource: 'familyPersonTags',
        default: []
      }
    ],
    spec: {
      source: { type: 'member' },
      where: [{ field: 'personTags.tag', op: 'in', value: { param: 'tags' } }],
      reduce: { kind: 'any' }
    }
  },
  {
    id: 'member.deceased',
    title: 'Deceased',
    description:
      'Family members recorded as deceased. Most useful inverted, to narrow another overlay to living relatives.',
    category: 'Person',
    roles: 'member',
    intents: [
      'who is deceased',
      'show deceased relatives',
      'highlight the people who have died',
      'which family members are no longer living'
    ],
    pill: 'DEC',
    paramDefs: [],
    spec: {
      source: { type: 'member' },
      where: [{ field: '$deceased', op: 'equals', value: true }],
      reduce: { kind: 'any' }
    }
  },
  {
    id: 'member.ageBand',
    title: 'Aged over',
    description:
      'Family members at or above a given age, computed from date of birth.',
    category: 'Person',
    roles: 'member',
    intents: [
      'who is over eighteen',
      'show adults only',
      'highlight relatives above a certain age',
      'which family members are adults',
      'show me people old enough to consent'
    ],
    pill: 'AGE',
    paramDefs: [
      {
        key: 'minAge',
        type: 'int',
        label: 'Aged at least',
        min: 0,
        max: 120,
        default: 18,
        bands: [16, 18, 40, 60]
      }
    ],
    spec: {
      source: { type: 'member' },
      where: [
        { field: '$age', op: 'greaterThanOrEqual', value: { param: 'minAge' } }
      ],
      reduce: { kind: 'any' }
    }
  },

  // ── Clinical ────────────────────────────────────────────────────────────
  {
    id: 'clinical.phenotypePresent',
    title: 'Has phenotype',
    description:
      'Family members with the chosen phenotype recorded as observed. Absent phenotypes, which are a distinct clinical statement, are not matched.',
    category: 'Clinical',
    roles: 'member',
    intents: [
      'show me everyone with this phenotype',
      'who has a given phenotype',
      'highlight relatives with a feature',
      'which people have that HPO term',
      'show phenotype carriers on the pedigree'
    ],
    pill: 'PHENO',
    paramDefs: [
      {
        key: 'phenotypeId',
        type: 'phenotypeId',
        label: 'Phenotype',
        optionsSource: 'familyPhenotypes',
        default: null
      }
    ],
    spec: {
      source: { type: 'memberArray', path: 'phenotypes' },
      where: [
        { field: 'id', op: 'equals', value: { param: 'phenotypeId' } },
        { field: 'observed', op: 'notEquals', value: false }
      ],
      reduce: { kind: 'any' }
    }
  },
  {
    id: 'clinical.genotypeCarrier',
    title: 'Carrier status for gene',
    description:
      'Family members whose recorded genotype for the chosen gene is a positive or a negative carrier result.',
    category: 'Clinical',
    roles: 'member',
    intents: [
      'who carries this gene',
      'show carriers of a gene',
      'which relatives tested positive',
      'who tested negative for the family variant',
      'highlight gene carriers on the pedigree'
    ],
    pill: 'GENE',
    paramDefs: [
      {
        key: 'gene',
        type: 'gene',
        label: 'Gene',
        optionsSource: 'familyGenes',
        default: null
      },
      {
        key: 'result',
        type: 'enum',
        label: 'Result',
        options: [
          { value: 'positive', label: 'Carries (+)' },
          { value: 'negative', label: 'Does not carry (-)' }
        ],
        default: 'positive'
      }
    ],
    spec: {
      source: { type: 'memberArray', path: 'genotypes' },
      where: [
        { field: '$gene', op: 'equals', value: { param: 'gene' } },
        { field: '$carrierResult', op: 'equals', value: { param: 'result' } }
      ],
      reduce: { kind: 'any' }
    }
  },
  {
    id: 'clinical.diagnosisStatus',
    title: 'Diagnosis status',
    description:
      'Family members whose record for the chosen condition carries one of the selected affected statuses.',
    category: 'Clinical',
    roles: 'member',
    intents: [
      'who is affected by this condition',
      'show carriers of a condition',
      'which relatives are unaffected',
      'highlight people by diagnosis status',
      'show me the affected family members'
    ],
    pill: 'DX',
    paramDefs: [
      {
        key: 'diseaseId',
        type: 'diseaseId',
        label: 'Condition',
        optionsSource: 'familyDiagnoses',
        default: null
      },
      {
        key: 'statuses',
        type: 'multiEnum',
        label: 'Affected status',
        options: [
          { value: 'affected', label: 'Affected' },
          { value: 'carrier', label: 'Carrier' },
          { value: 'unaffected', label: 'Unaffected' },
          { value: 'unknown', label: 'Unknown' }
        ],
        default: ['affected']
      }
    ],
    spec: {
      source: { type: 'memberArray', path: 'diagnoses' },
      where: [
        { field: 'id', op: 'equals', value: { param: 'diseaseId' } },
        { field: 'affectedStatus', op: 'in', value: { param: 'statuses' } }
      ],
      reduce: { kind: 'any' }
    }
  }
]);

/** Summaries for the Copilot vocabulary artifact: no specs, no family data. */
export const overlaySummaries = () =>
  DATA_OVERLAY_PRESETS.map((p) => ({
    id: p.id,
    title: p.title,
    description: p.description,
    category: p.category,
    roles: p.roles,
    intents: p.intents,
    paramDefs: (p.paramDefs || []).map((d) => ({
      key: d.key,
      type: d.type,
      label: d.label,
      min: d.min,
      max: d.max,
      default: d.default,
      options: d.options
    }))
  }));

export const presetById = (id) =>
  DATA_OVERLAY_PRESETS.find((p) => p.id === id) || null;
